import type { View } from "@gooner/shared";

/** Much shorter than the editor's 1200ms autosave: a screen should track the drag, while
 *  the row only needs to catch up once the hand stops. */
const LIVE_PUSH_MS = 250;

/** The half of a ControlConn this needs. Narrow on purpose — it is what makes the rule
 *  below testable without a socket. */
interface ViewPusherTarget {
  pushUpdate: (view: View) => void;
}

/** What the store holds, and which generation that is. */
interface ViewSnapshot {
  view: View | null;
  rev: number;
}

export interface ViewPusher {
  /** Debounced. Sends the store's CURRENT view, if the generation has moved since the
   *  last send. */
  schedule: () => void;
  /** Immediate and unconditional — for a socket that just (re)connected. */
  flushNow: () => void;
  /** Drop anything pending. For unmount. */
  cancel: () => void;
}

/**
 * Decides what goes on the wire, and when.
 *
 * The rule is one sentence: **read at fire time, never at schedule time**. A push built
 * from a snapshot captured when the timer was set carries content the store may already
 * have moved past — a later edit, a load, or (the expensive one) a promotion that changed
 * the view's very id. The screens follow the saved view, so a push addressed to the
 * expiring temporary copy reaches nobody, is never retried, and leaves the wall showing
 * an edit older than the one on the operator's screen. That is the same divergence
 * `viewStore`'s `rev` guard prevents on responses, and it is why the generation is read
 * here too rather than assumed.
 *
 * This lives outside the React hook because the hook cannot be driven headlessly and this
 * rule is the part with a race in it.
 */
export function createViewPusher(
  target: ViewPusherTarget,
  read: () => ViewSnapshot,
  delayMs: number = LIVE_PUSH_MS,
): ViewPusher {
  let timer: ReturnType<typeof setTimeout> | null = null;
  // The generation of the last thing actually sent. Monotonic, like the store's own, so a
  // schedule with nothing new behind it is a no-op rather than a duplicate frame.
  let sentRev = -1;

  const sendNow = (force: boolean): void => {
    // Read HERE — inside the timer's callback, at the moment of sending. Everything this
    // module is for is in the placement of this line.
    const { view, rev } = read();
    if (!view) return;
    if (!force && rev <= sentRev) return;
    sentRev = rev;
    target.pushUpdate(view);
  };

  return {
    schedule: () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        sendNow(false);
      }, delayMs);
    },
    flushNow: () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      // Forced: a screen that just joined has been sent `config` built from the last
      // SAVED state, so it needs this even when the generation hasn't moved.
      sendNow(true);
    },
    cancel: () => {
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
}
